// Settings — per-brand Meta credentials + ad defaults + video voice / b-roll.

import { useEffect, useState } from 'react';
import Beacon from '../components/Beacon.jsx';
import { PALETTE } from '../palette.js';
import { Card, Pill } from '../components/primitives.jsx';
import { api, getToken } from '../api.js';
import SaasSettings from './SaasSettings.jsx';

const TABS = [
  { id: 'ads',    label: 'Ads' },
  { id: 'videos', label: 'Videos' },
];

const PROJECT_TYPES = [
  { id: 'kitchen',  label: 'Kitchen' },
  { id: 'addition', label: 'Addition' },
  { id: 'sunroom',  label: 'Sunroom' },
  { id: 'deck',     label: 'Deck' },
];

export default function Settings({ session, navigate }) {
  if (session?.user?.auth?.type === 'supabase') {
    return <SaasSettings session={session} navigate={navigate} />;
  }
  return <LegacyBrandSettings />;
}

function LegacyBrandSettings() {
  const [brands, setBrands] = useState(null);
  const [brandSlug, setBrandSlug] = useState(() => localStorage.getItem('settings:lastBrand') || '');
  const [tab, setTab] = useState('ads');
  const [config, setConfig] = useState(null);
  const [video, setVideo] = useState(null);

  useEffect(() => {
    api.get('/api/clients').then((r) => setBrands(r.clients || [])).catch(() => setBrands([]));
  }, []);

  useEffect(() => {
    if (!brandSlug) { setConfig(null); setVideo(null); return; }
    localStorage.setItem('settings:lastBrand', brandSlug);
    // Clear immediately so the form doesn't briefly render the previous
    // brand's values during the new brand's fetch.
    setConfig(null); setVideo(null);
    Promise.all([
      api.get(`/api/meta/config/${encodeURIComponent(brandSlug)}`).catch(() => null),
      api.get(`/api/brands/${encodeURIComponent(brandSlug)}/video-settings`).catch(() => null),
    ]).then(([c, v]) => { setConfig(c); setVideo(v); });
  }, [brandSlug]);

  return (
    <div style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Settings</h1>
        <Pill tone="accent">Per-brand</Pill>
      </header>

      {/* Brand picker */}
      <Card style={{ padding: 18, marginBottom: 22, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Eyebrow>Brand</Eyebrow>
        <select
          value={brandSlug}
          onChange={(e) => setBrandSlug(e.target.value)}
          style={{
            padding: '8px 12px', fontSize: 13, fontFamily: 'Inter, sans-serif',
            color: brandSlug ? PALETTE.ink : PALETTE.ink3,
            background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
            borderRadius: 9, outline: 'none', cursor: 'pointer',
            minWidth: 280,
          }}
        >
          <option value="">{brands == null ? 'Loading…' : 'Pick a brand…'}</option>
          {(brands || []).map((b) => (
            <option key={b.slug} value={b.slug}>{b.name}</option>
          ))}
        </select>
      </Card>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${PALETTE.line}`, marginBottom: 22 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '11px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em',
            fontWeight: 700, color: tab === t.id ? PALETTE.accent : PALETTE.ink3,
            border: 'none', background: 'none',
            borderBottom: `2px solid ${tab === t.id ? PALETTE.accent : 'transparent'}`,
            cursor: 'pointer', marginBottom: -1, fontFamily: 'Inter, sans-serif',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      {!brandSlug ? (
        <EmptyHint>Pick a brand to edit settings.</EmptyHint>
      ) : tab === 'ads' ? (
        <AdsTab brandSlug={brandSlug} config={config} setConfig={setConfig} />
      ) : (
        <VideosTab brandSlug={brandSlug} video={video} setVideo={setVideo} />
      )}
    </div>
  );
}

// ─── ADS TAB ──────────────────────────────────────────────────────────────
function AdsTab({ brandSlug, config, setConfig }) {
  if (config === null) return <EmptyHint>Loading…</EmptyHint>;
  return (
    <>
      <CredentialsPanel brandSlug={brandSlug} config={config} setConfig={setConfig} />
      <DefaultsPanel brandSlug={brandSlug} config={config} setConfig={setConfig} />
    </>
  );
}

function CredentialsPanel({ brandSlug, config, setConfig }) {
  const c = config || {};
  const [appId, setAppId] = useState(c.appId || '');
  const [adAccountId, setAdAccountId] = useState(c.adAccountId || '');
  const [pageId, setPageId] = useState(c.pageId || '');
  const [pixelId, setPixelId] = useState(c.pixelId || '');
  const [token, setToken] = useState('');
  const [secret, setSecret] = useState('');
  const [toast, setToast] = useState(null);

  // Re-sync whenever the loaded config changes (which happens after a
  // brand-switch fetch completes — depending only on brandSlug fired
  // before the new config had arrived, leaving stale values in the
  // form fields from the previous brand).
  useEffect(() => {
    setAppId(c.appId || '');
    setAdAccountId(c.adAccountId || '');
    setPageId(c.pageId || '');
    setPixelId(c.pixelId || '');
    setToken(''); setSecret('');
  }, [brandSlug, c.appId, c.adAccountId, c.pageId, c.pixelId]);

  async function save() {
    const cleanedAcct = adAccountId.trim().replace(/^act_/, '');
    if (!cleanedAcct || !/^\d+$/.test(cleanedAcct)) {
      setToast({ tone: 'err', msg: 'Ad Account ID is required (numeric only).' });
      return;
    }
    setToast({ tone: 'info', msg: 'Saving…' });
    const patch = {
      appId: appId.trim(),
      adAccountId: cleanedAcct,
      pageId: pageId.trim(),
      pixelId: pixelId.trim(),
    };
    if (token.trim()) patch.accessToken = token.trim();
    if (secret.trim()) patch.appSecret = secret.trim();
    try {
      const apiToken = getToken();
      const res = await fetch(`/api/meta/config/${encodeURIComponent(brandSlug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}) },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const j = await res.json();
      setConfig(j);
      setToken(''); setSecret('');
      setToast({ tone: 'ok', msg: 'Saved.' });
    } catch (err) {
      setToast({ tone: 'err', msg: `Save failed: ${err.message}` });
    }
  }

  async function test() {
    setToast({ tone: 'info', msg: 'Testing the Meta connection…' });
    try {
      const r = await api.get(`/api/meta/verify/${encodeURIComponent(brandSlug)}`);
      if (r.ok) {
        const acct = r.account || {};
        setToast({ tone: 'ok', msg: `✓ Connected to "${acct.name || 'account'}" (${acct.currency || ''} · ${acct.timezone_name || ''})` });
      } else {
        setToast({ tone: 'err', msg: `Failed: ${r.error || 'unknown'}` });
      }
    } catch (err) {
      setToast({ tone: 'err', msg: `Failed: ${err.message}` });
    }
  }

  return (
    <Panel
      title="Meta Ads credentials"
      sub={<>Token stored in <code style={mono}>config/.meta-ads.secrets.json</code>. Leave token fields blank to keep existing values.</>}
      status={c.configured ? { tone: 'ok', label: '✓ Configured' } : { tone: 'warn', label: '⚠ Not configured' }}
    >
      <FormGrid>
        <Field label="Meta App ID">
          <Input value={appId} onChange={setAppId} />
        </Field>
        <Field label="Ad Account ID" hint="Numeric only — we add the act_ prefix automatically.">
          <Input value={adAccountId} onChange={setAdAccountId} placeholder="e.g. 1234567890123456" />
        </Field>
        <Field label="Page ID">
          <Input value={pageId} onChange={setPageId} />
        </Field>
        <Field label="Pixel ID">
          <Input value={pixelId} onChange={setPixelId} />
        </Field>
        <Field label="Access Token" full>
          <Input
            value={token} onChange={setToken} type="password" autoComplete="off"
            placeholder={c.accessTokenMasked ? `Keep existing: ${c.accessTokenMasked}` : 'Paste long-lived system user token'}
          />
        </Field>
        <Field label="App Secret (optional)" full>
          <Input
            value={secret} onChange={setSecret} type="password" autoComplete="off"
            placeholder={c.appSecretMasked ? `Keep existing: ${c.appSecretMasked}` : 'Only needed for app-level calls'}
          />
        </Field>
      </FormGrid>
      <Actions toast={toast}>
        <button onClick={save} style={inkBtn}>Save credentials</button>
        <button onClick={test} style={ghostBtn}>Test connection</button>
      </Actions>
    </Panel>
  );
}

function DefaultsPanel({ brandSlug, config, setConfig }) {
  const d = config?.defaults || {};
  const [dailyBudget, setDailyBudget] = useState(d.dailyBudget ?? 10);
  const [radiusKm, setRadiusKm] = useState(d.radiusKm ?? 50);
  const [city, setCity] = useState(d.city || '');
  const [ageMin, setAgeMin] = useState(d.ageMin ?? 25);
  const [ageMax, setAgeMax] = useState(d.ageMax ?? 65);
  const [currency, setCurrency] = useState(d.currency || 'USD');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setDailyBudget(d.dailyBudget ?? 10);
    setRadiusKm(d.radiusKm ?? 50);
    setCity(d.city || '');
    setAgeMin(d.ageMin ?? 25);
    setAgeMax(d.ageMax ?? 65);
    setCurrency(d.currency || 'USD');
  }, [brandSlug, d.dailyBudget, d.radiusKm, d.city, d.ageMin, d.ageMax, d.currency]);

  async function save() {
    setToast({ tone: 'info', msg: 'Saving…' });
    try {
      const apiToken = getToken();
      const patch = {
        defaults: {
          dailyBudget: Number(dailyBudget) || 10,
          radiusKm: Number(radiusKm) || 50,
          city: city.trim(),
          ageMin: Number(ageMin) || 25,
          ageMax: Number(ageMax) || 65,
          currency: currency.trim() || 'USD',
        },
      };
      const res = await fetch(`/api/meta/config/${encodeURIComponent(brandSlug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}) },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const j = await res.json();
      setConfig(j);
      setToast({ tone: 'ok', msg: 'Saved.' });
    } catch (err) {
      setToast({ tone: 'err', msg: `Save failed: ${err.message}` });
    }
  }

  return (
    <Panel
      title="Ad defaults"
      sub="Used when publishing videos. Override per-video in the publish modal."
    >
      <FormGrid>
        <Field label="Daily budget (USD)">
          <Input type="number" min="1" step="1" value={dailyBudget} onChange={setDailyBudget} />
        </Field>
        <Field label="Radius (km)">
          <Input type="number" min="10" max="80" step="1" value={radiusKm} onChange={setRadiusKm} />
        </Field>
        <Field label="Default city">
          <Input value={city} onChange={setCity} placeholder="Ottawa, ON, Canada" />
        </Field>
        <Field label="Age min">
          <Input type="number" min="18" max="65" value={ageMin} onChange={setAgeMin} />
        </Field>
        <Field label="Age max">
          <Input type="number" min="18" max="65" value={ageMax} onChange={setAgeMax} />
        </Field>
        <Field label="Currency">
          <Input value={currency} onChange={setCurrency} />
        </Field>
      </FormGrid>
      <Actions toast={toast}>
        <button onClick={save} style={inkBtn}>Save defaults</button>
      </Actions>
    </Panel>
  );
}

// ─── VIDEOS TAB ───────────────────────────────────────────────────────────
function VideosTab({ brandSlug, video, setVideo }) {
  if (video === null) return <EmptyHint>Loading…</EmptyHint>;
  return (
    <>
      <VoicePanel brandSlug={brandSlug} video={video} setVideo={setVideo} />
      <BrollPanel brandSlug={brandSlug} video={video} setVideo={setVideo} />
    </>
  );
}

function VoicePanel({ brandSlug, video, setVideo }) {
  const profiles = video?.profiles || [];
  const settings = video?.settings || { voiceProfileId: '', brollFolders: {} };
  const [voiceProfileId, setVoiceProfileId] = useState(settings.voiceProfileId || '');
  const [toast, setToast] = useState(null);

  useEffect(() => { setVoiceProfileId(settings.voiceProfileId || ''); }, [brandSlug, settings.voiceProfileId]);

  async function save() {
    setToast({ tone: 'info', msg: 'Saving…' });
    try {
      const apiToken = getToken();
      const res = await fetch(`/api/brands/${encodeURIComponent(brandSlug)}/video-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}) },
        body: JSON.stringify({ voiceProfileId }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const j = await res.json();
      setVideo({ ...video, settings: j.settings });
      setToast({ tone: 'ok', msg: 'Saved.' });
    } catch (err) {
      setToast({ tone: 'err', msg: `Save failed: ${err.message}` });
    }
  }

  return (
    <Panel
      title="Voice profile"
      sub="One voice per brand — used by default for every render. Override per-video on the Videos page."
    >
      <FormGrid>
        <Field label="ElevenLabs voice" full>
          <select
            value={voiceProfileId}
            onChange={(e) => setVoiceProfileId(e.target.value)}
            style={inputStyle}
          >
            <option value="">{profiles.length ? 'Pick a voice…' : 'No profiles configured'}</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {p.voiceId}</option>
            ))}
          </select>
        </Field>
      </FormGrid>
      <Actions toast={toast}>
        <button onClick={save} style={inkBtn}>Save voice</button>
      </Actions>
    </Panel>
  );
}

function BrollPanel({ brandSlug, video, setVideo }) {
  const settings = video?.settings || { brollFolders: {} };
  const [folders, setFolders] = useState(settings.brollFolders || {});
  const [toast, setToast] = useState(null);

  useEffect(() => { setFolders(settings.brollFolders || {}); }, [brandSlug, settings.brollFolders]);

  function setFolder(id, value) {
    setFolders((prev) => ({ ...prev, [id]: value }));
  }

  async function save() {
    setToast({ tone: 'info', msg: 'Saving…' });
    try {
      const apiToken = getToken();
      const cleaned = {};
      for (const pt of PROJECT_TYPES) {
        cleaned[pt.id] = String(folders[pt.id] || '').trim();
      }
      const res = await fetch(`/api/brands/${encodeURIComponent(brandSlug)}/video-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}) },
        body: JSON.stringify({ brollFolders: cleaned }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const j = await res.json();
      setVideo({ ...video, settings: j.settings });
      setToast({ tone: 'ok', msg: 'Saved.' });
    } catch (err) {
      setToast({ tone: 'err', msg: `Save failed: ${err.message}` });
    }
  }

  return (
    <Panel
      title="B-roll folders"
      sub="Cloudinary folder name per project type. Just the folder — no leading slash. Videos for that project type pull clips from the matching folder."
    >
      <FormGrid>
        {PROJECT_TYPES.map((pt) => (
          <Field key={pt.id} label={pt.label}>
            <Input
              value={folders[pt.id] || ''}
              onChange={(v) => setFolder(pt.id, v)}
              placeholder={`e.g. ${pt.id}-reels-brand`}
            />
          </Field>
        ))}
      </FormGrid>
      <Actions toast={toast}>
        <button onClick={save} style={inkBtn}>Save b-roll folders</button>
      </Actions>
    </Panel>
  );
}

// ─── ATOMS ────────────────────────────────────────────────────────────────
function Panel({ title, sub, status, children }) {
  return (
    <Card style={{ padding: 24, marginBottom: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: sub ? 6 : 14, gap: 12,
      }}>
        <h3 style={{
          fontFamily: 'Instrument Serif, serif', fontSize: 24, fontWeight: 400,
          margin: 0, color: PALETTE.ink, letterSpacing: '-0.005em',
        }}>{title}</h3>
        {status && <StatusPill {...status} />}
      </div>
      {sub && (
        <p style={{ fontSize: 12.5, color: PALETTE.ink3, margin: '0 0 16px', lineHeight: 1.5 }}>
          {sub}
        </p>
      )}
      {children}
    </Card>
  );
}

function StatusPill({ tone, label }) {
  const map = {
    ok:   { bg: PALETTE.successSoft, fg: PALETTE.success, bd: '#CFD9B4' },
    warn: { bg: PALETTE.warnSoft,    fg: PALETTE.warn,    bd: '#EBDCB2' },
    err:  { bg: PALETTE.dangerSoft,  fg: PALETTE.danger,  bd: '#E8C9C1' },
  };
  const t = map[tone] || map.ok;
  return (
    <span style={{
      padding: '3px 10px', fontSize: 11, fontWeight: 500,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      borderRadius: 999, fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function FormGrid({ children }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14,
      marginBottom: 16,
    }}>
      {children}
    </div>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <Eyebrow style={{ fontSize: 9.5, color: PALETTE.ink3, marginBottom: 6 }}>{label}</Eyebrow>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 4, fontStyle: 'italic' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, autoComplete, min, max, step }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      min={min} max={max} step={step}
      style={inputStyle}
    />
  );
}

function Actions({ toast, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {children}
      {toast && (
        <span role={toast.tone === 'err' ? 'alert' : 'status'} style={{
          fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
          color: toast.tone === 'ok' ? PALETTE.success : toast.tone === 'err' ? PALETTE.danger : PALETTE.ink3,
        }}>{toast.tone === 'info' ? <Beacon size="inline" tone="neutral" delayMs={0} slowAfterMs={toast.msg.startsWith('Testing') ? undefined : null} /> : null}{toast.msg}</span>
      )}
    </div>
  );
}

function EmptyHint({ children }) {
  return (
    <div style={{
      padding: '20px 24px',
      background: PALETTE.surfaceAlt, border: `1px dashed ${PALETTE.line}`, borderRadius: 12,
      color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic',
    }}>{children}</div>
  );
}

function Eyebrow({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  fontFamily: 'Inter, sans-serif', color: PALETTE.ink,
  background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
  borderRadius: 9, outline: 'none', boxSizing: 'border-box',
};
const mono = {
  fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
  background: PALETTE.surfaceAlt, padding: '1px 5px', borderRadius: 4,
  border: `1px solid ${PALETTE.line}`, color: PALETTE.ink2,
};
const inkBtn = {
  padding: '9px 16px', fontSize: 13, fontWeight: 500,
  background: PALETTE.ink, color: '#FAF8F5',
  border: `1px solid ${PALETTE.ink}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
const ghostBtn = {
  padding: '9px 16px', fontSize: 13, fontWeight: 500,
  background: 'transparent', color: PALETTE.ink,
  border: `1px solid ${PALETTE.line}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
